import { cn } from "@/common/utils/css.utils";
import { cva, VariantProps } from "class-variance-authority";
import { Ref, ComponentProps, forwardRef } from "react";

const RadioButtonStyle = cva(
  cn(
    "grid h-5 w-5 shrink-0 cursor-pointer appearance-none place-content-center rounded-full border-2",
    "before:h-5 before:w-5 before:scale-0 before:rounded-full before:border-[3px] before:border-white before:transition-transform",
    "checked:before:scale-75",
    "disabled:checked:before:border-grey-white disabled:cursor-not-allowed disabled:border-grey-300 disabled:bg-grey-100 disabled:checked:bg-grey-300"
  ),
  {
    variants: {
      color: {
        brand: "",
        curious: "",
      },
      valid: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      /* Valid, brand or curious */
      {
        valid: true,
        color: ["brand", "curious"],
        className: "border-grey-500",
      },
      /* Valid, brand */
      {
        valid: true,
        color: "brand",
        className:
          "checked:border-brand-500 checked:bg-brand-500 md:hover:border-brand-300 md:checked:hover:bg-brand-300",
      },
      /* Valid, curious */
      {
        valid: true,
        color: "curious",
        className:
          "checked:border-curious-500 checked:bg-curious-500 md:hover:border-curious-300 md:checked:hover:bg-curious-300",
      },
      /* Not valid, brand or curious */
      {
        valid: false,
        color: ["brand", "curious"],
        className:
          "border-attention-500 checked:border-attention-500 checked:bg-attention-500 md:hover:border-attention-300 md:checked:hover:bg-attention-300",
      },
    ],
    defaultVariants: {
      color: "brand",
      valid: true,
    },
  }
);

type RadioButtonBaseProps = {
  ref?: Ref<HTMLInputElement>;
  elementRef?: Ref<HTMLInputElement>;
} & Omit<ComponentProps<"input">, "ref" | "color"> &
  VariantProps<typeof RadioButtonStyle>;

export const RadioButtonBase = ({
  elementRef,
  color,
  valid,
  className,
  ...props
}: RadioButtonBaseProps) => {
  return (
    <input
      {...props}
      type="radio"
      ref={elementRef}
      className={cn(RadioButtonStyle({ color, valid }), className)}
    />
  );
};

RadioButtonBase.displayName = "RadioButton";

export type RadioButtonProps = Omit<RadioButtonBaseProps, "elementRef">;

/**
 * Simple radio button.
 */
export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  (props, ref) => <RadioButtonBase {...props} elementRef={ref} />
);
